import { FC } from "react";
import {
    Autocomplete,
    AutocompleteRenderInputParams,
    Box,
    Button,
    Chip,
    MenuItem,
    Select,
    TextField,
} from "@mui/material";
import { Formik } from "formik";
import { OralViewModel } from "../../../../models/OralView.model";
import { unityOptions } from "../../../../models/unity-options.model";
import periodsOral from "../../../../utils/periodsOral";
import { getPeriodLabel } from "../../../../utils/period-label";
import { AppModal } from "../../../../components/ui";

type Props = {
    open: boolean;
    onClose: () => void;
    productViewModel: OralViewModel;
    setProductViewModel: (model: OralViewModel) => void;
    productViewInitial: OralViewModel;
    productSchema: any;
    formik: any;
    btnSaveDisabled: boolean;
    selectedClasses: string[];
    filterArray: string[];
    setClasses: (value: string) => void;
    saveFilter: (newValue: string[]) => void;
    handleAddClass: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    handleRemoveClass: (option: string) => void;
    autoCompletefilter: () => void;
};

/** Modal de cadastro/edição de ativo oral (classificação + dados do produto). */
const OralFormModal: FC<Props> = ({
    open,
    onClose,
    productViewModel,
    setProductViewModel,
    productViewInitial,
    productSchema,
    formik,
    btnSaveDisabled,
    selectedClasses,
    filterArray,
    setClasses,
    saveFilter,
    handleAddClass,
    handleRemoveClass,
    autoCompletefilter,
}) => {
    const { errors, touched, handleSubmit, getFieldProps } = formik;

    const renderTags = () =>
        selectedClasses.map((option, index) => (
            <Chip key={index} label={option} color={"primary"} onDelete={() => handleRemoveClass(option)} />
        ));

    return (
        <AppModal
            open={open}
            onClose={onClose}
            kicker={productViewModel.id ? "Editar" : "Cadastrar"}
            title={productViewModel.id ? "Editar ativo oral" : "Novo ativo oral"}
            maxWidth={860}
            footer={
                <>
                    <Button variant="outlined" color="secondary" onClick={onClose} sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}>
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={btnSaveDisabled}
                        onClick={() => handleSubmit()}
                        sx={{ height: 44, borderRadius: "12px", fontWeight: 600 }}
                    >
                        Salvar
                    </Button>
                </>
            }
        >
            <Formik initialValues={productViewInitial} validationSchema={productSchema} onSubmit={() => {}}>
                <form noValidate onSubmit={handleSubmit}>
                    <Box sx={{ width: "100%" }}>
                        <Autocomplete
                            multiple
                            freeSolo
                            sx={{ gridColumn: "span 2" }}
                            value={selectedClasses}
                            renderTags={renderTags}
                            options={filterArray}
                            filterOptions={(options, params) => {
                                const filtered = options.filter((option) =>
                                    option.toLowerCase().includes(params.inputValue.toLowerCase())
                                );
                                return filtered;
                            }}
                            renderOption={(props, option) => <li {...props}>{option}</li>}
                            onChange={(event, newValue) => {
                                saveFilter(newValue);
                            }}
                            renderInput={(params: AutocompleteRenderInputParams) => (
                                <TextField
                                    {...params}
                                    label={"Classificação"}
                                    variant="outlined"
                                    color="primary"
                                    onKeyDown={handleAddClass}
                                    onClick={autoCompletefilter}
                                    onChange={(e) => setClasses(e.target.value)}
                                    InputProps={{ ...params.InputProps }}
                                />
                            )}
                        />

                        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 3, mt: 3 }}>
                            <TextField
                                label={"Nome do produto"}
                                {...getFieldProps("productName")}
                                value={productViewModel.name}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onChange={(event) => {
                                    productViewModel.name = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.name && errors.name)}
                                helperText={touched.name && errors.name}
                            />

                            <Select
                                fullWidth
                                value={productViewModel.unity}
                                onChange={(event) => {
                                    productViewModel.unity = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                            >
                                {unityOptions.map((option) => (
                                    <MenuItem key={option.value} value={option.value}>
                                        {option.label}
                                    </MenuItem>
                                ))}
                            </Select>

                            <TextField
                                label={"Dosagem minima"}
                                {...getFieldProps("minimumDosage")}
                                value={productViewModel.min}
                                variant="outlined"
                                type="number"
                                color="primary"
                                fullWidth
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                onChange={(event) => {
                                    productViewModel.min = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.min && errors.min)}
                                helperText={touched.min && errors.min}
                            />

                            <TextField
                                label={"Dosagem máxima"}
                                {...getFieldProps("maxDosage")}
                                value={productViewModel.max}
                                variant="outlined"
                                type="number"
                                color="primary"
                                fullWidth
                                inputProps={{ inputMode: "numeric", pattern: "[0-9]*" }}
                                onChange={(event) => {
                                    productViewModel.max = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.max && errors.max)}
                                helperText={touched.max && errors.max}
                            />

                            <TextField
                                label={"Mecanismo de ação"}
                                {...getFieldProps("mechanismAction")}
                                value={productViewModel.mechanism}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                multiline
                                rows={4}
                                onChange={(event) => {
                                    productViewModel.mechanism = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.mechanism && errors.mechanism)}
                                helperText={touched.mechanism && errors.mechanism}
                            />

                            <TextField
                                label={"Descrição"}
                                {...getFieldProps("description")}
                                value={productViewModel.description}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                multiline
                                rows={4}
                                onChange={(event) => {
                                    productViewModel.description = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.description && errors.description)}
                                helperText={touched.description && errors.description}
                            />

                            <TextField
                                label={"Fonte"}
                                {...getFieldProps("source")}
                                value={productViewModel.source}
                                variant="outlined"
                                color="primary"
                                fullWidth
                                onChange={(event) => {
                                    productViewModel.source = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.source && errors.source)}
                                helperText={touched.source && errors.source}
                            />

                            <TextField
                                select
                                fullWidth
                                {...getFieldProps("period")}
                                name={"period"}
                                label="Período"
                                value={productViewModel.period}
                                onChange={(event) => {
                                    productViewModel.period = event.target.value;
                                    setProductViewModel({ ...productViewModel });
                                }}
                                error={Boolean(touched.period && errors.period)}
                                helperText={touched.period && errors.period}
                            >
                                {periodsOral.map((option) => (
                                    <MenuItem key={option} value={option}>
                                        {getPeriodLabel(option)}
                                    </MenuItem>
                                ))}
                            </TextField>
                        </Box>
                    </Box>
                </form>
            </Formik>
        </AppModal>
    );
};

export default OralFormModal;
